import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckCircle2, XCircle, Camera, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api, { fileUrl } from './api';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/use-toast';
import { acquireLocation, locHint } from './geo';

const VerifyOperative = () => {
  const { user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loc, setLoc] = useState({ status: 'idle', lat: null, lng: null, error: null });
  const scannerRef = useRef(null);

  const getLocation = async () => {
    setLoc((s) => ({ ...s, status: 'acquiring' }));
    const g = await acquireLocation();
    setLoc({ status: g.lat != null ? 'ready' : 'error', lat: g.lat, lng: g.lng, error: g.error, accuracy: g.accuracy });
    return g;
  };

  useEffect(() => { if (!loading && !user) navigate('/auth?next=/verify'); }, [loading, user, navigate]);
  useEffect(() => { if (user) getLocation(); /* request GPS up front */ }, [user]);
  useEffect(() => () => { stopCam(); }, []);

  const doScan = async (token) => {
    if (!token) return;
    setBusy(true); await stopCam();
    try {
      // Use the location acquired up front; if missing, try once more now
      let g = loc;
      if (g.lat == null) g = await getLocation();
      if (g.lat == null) {
        setBusy(false);
        toast({ title: 'Location needed', description: locHint(g.error), variant: 'destructive' });
        return;
      }
      const r = await api.post('/verify/scan', { token: token.trim(), lat: g.lat, lng: g.lng, accuracy: g.accuracy });
      setResult(r.data);
    } catch (e) { toast({ title: 'Scan failed', description: e?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const startCam = async () => {
    setResult(null); setScanning(true);
    setTimeout(async () => {
      try {
        const el = document.getElementById('qr-reader');
        if (!el) return;
        scannerRef.current = new Html5Qrcode('qr-reader');
        await scannerRef.current.start({ facingMode: 'environment' }, { fps: 10, qrbox: 240 },
          (decoded) => { doScan(decoded); }, () => {});
      } catch (e) { setScanning(false); toast({ title: 'Camera unavailable', description: 'Use the manual code option below.', variant: 'destructive' }); }
    }, 100);
  };
  const stopCam = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } } catch (e) {}
    setScanning(false);
  };

  const verified = result && result.result === 'VERIFIED';
  return (
    <div className="min-h-[80vh] bg-[#eef2f6] py-10"><div className="mx-auto max-w-xl px-5">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#1b4f72]"><ArrowLeft size={16} /> Back</button>
      <div className="rounded-2xl bg-white p-7 shadow-md" data-testid="verify-page">
        <div className="flex items-center gap-3"><div className="rounded-full bg-[#1b4f72] p-2 text-white"><ShieldCheck size={22} /></div><div><h1 className="font-display text-xl font-bold text-[#1b4f72]">Verify Operative ID</h1><p className="text-sm text-gray-500">Scan the operative's QR code on arrival to confirm their identity.</p></div></div>

        {!result && (
          <div className="mt-6">
            <div className={`mb-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm ${loc.status === 'ready' ? 'bg-green-50 text-green-800' : loc.status === 'acquiring' ? 'bg-blue-50 text-blue-800' : 'bg-amber-50 text-amber-800'}`} data-testid="loc-status">
              <span className="min-w-0">
                {loc.status === 'ready' && `📍 Location ready${loc.accuracy ? ` (±${Math.round(loc.accuracy)}m)` : ''}`}
                {loc.status === 'acquiring' && '📍 Getting your location…'}
                {(loc.status === 'error' || loc.status === 'idle') && `📍 ${locHint(loc.error)}`}
              </span>
              {loc.status !== 'ready' && loc.status !== 'acquiring' && <button data-testid="retry-loc-btn" onClick={getLocation} className="shrink-0 rounded-full bg-[#1b4f72] px-4 py-1.5 text-xs font-semibold text-white">Enable location</button>}
            </div>
            {scanning ? (
              <>
                <div id="qr-reader" data-testid="qr-reader" className="overflow-hidden rounded-xl" />
                <button data-testid="stop-scan-btn" onClick={stopCam} className="mt-3 w-full rounded-full border border-gray-300 px-6 py-2.5 text-sm font-semibold text-gray-600">Stop Camera</button>
              </>
            ) : (
              <button data-testid="start-scan-btn" onClick={startCam} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#3a9b5c] px-6 py-3 font-semibold text-white hover:bg-[#318150]"><Camera size={18} /> Scan QR with Camera</button>
            )}
            <div className="mt-5 border-t border-gray-100 pt-4">
              <label className="mb-1.5 block text-sm font-medium text-gray-700">Camera blocked? Paste the code manually</label>
              <textarea data-testid="manual-code-input" rows={3} value={manual} onChange={(e) => setManual(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs" placeholder="Paste the operative's verification code…" />
              <button data-testid="manual-verify-btn" disabled={busy} onClick={() => doScan(manual)} className="mt-2 rounded-full bg-[#1b4f72] px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Verifying…' : 'Verify Code'}</button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6" data-testid="verify-result">
            <div className={`flex items-center gap-3 rounded-xl p-4 ${verified ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`} data-testid={verified ? 'result-verified' : 'result-not-verified'}>
              {verified ? <CheckCircle2 size={28} /> : <XCircle size={28} />}
              <div><p className="text-lg font-bold">{verified ? 'VERIFIED' : 'NOT VERIFIED'}</p><p className="text-sm">{verified ? 'This operative is confirmed for the job.' : 'Do not proceed — this could not be verified.'}</p></div>
            </div>
            {result.operative && (
              <div className="mt-4 flex items-center gap-4 rounded-xl bg-gray-50 p-4">
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">{result.operative.photoFileId ? <img src={fileUrl(result.operative.photoFileId)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-[#3a9b5c]"><ShieldCheck size={20} /></div>}</div>
                <div><p className="font-semibold text-[#1b4f72]">{result.operative.name}</p><p className="font-mono text-sm text-gray-500">{result.operative.reg} · {result.operative.trade}</p>{result.jobRef && <p className="text-xs text-gray-400">Job {result.jobRef}{result.distanceM != null ? ` · ${result.distanceM}m away` : ''}</p>}</div>
              </div>
            )}
            <ul className="mt-4 space-y-2" data-testid="verify-checks">
              {result.checks.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><span className={c.pass ? 'text-[#3a9b5c]' : 'text-red-500'}>{c.pass ? <CheckCircle2 size={16} /> : <XCircle size={16} />}</span><span className={c.pass ? 'text-gray-700' : 'text-red-700'}>{c.label}{c.note ? ` — ${c.note}` : ''}</span></li>
              ))}
            </ul>
            <button data-testid="scan-again-btn" onClick={() => { setResult(null); setManual(''); }} className="mt-5 w-full rounded-full bg-[#3a9b5c] px-6 py-2.5 text-sm font-semibold text-white">Scan Another</button>
          </div>
        )}
      </div>
    </div></div>
  );
};

export default VerifyOperative;
